'use client'

import React, { useEffect, useState } from 'react'
import Text from '../../Text'
import CtaLoading from '../../CtaLoading'
import styles from './styles.module.css'
import { http } from '../../../utils/clientHttp'
import { useRegionCountryCode } from '../../RegionGeo'
import ContactFormSubmittedAlert from './ContactFormSubmittedAlert'
import InputField from './InputField'
import { PALETTE } from '../../../styles/constants'
import SelectField from './SelectField'
import Cta from '../../Cta'
import SimpleBlockContent from '../../SimpleBlockContent'
import HighlightedText from '../../Utils/HighlightedText'
import { Grow } from '@mui/material'
import PhoneIcon from '../../icons/PhoneIcon'
import EmailIcon from '../../icons/EmailIcon'
import ContactFormSubmittedModal from './ContactFormSubmittedModal'
import { pushDataLayerEvent } from '../../../utils/dataLayer'



function ContactForm({
  sideContent = { backgroundColor: { value: PALETTE.gray10 } },
  title = '',
  subtitle,
  categories = [],
  backgroundColor = { value: PALETTE.white },
  textColor = { value: PALETTE.gray80 },
  errorMessageText = 'Something went wrong. Please try again. If the error persists, please contact us.'
}) {
  // newRegion migrated to the RegionGeo seam (Phase C2 part 2): the region-store
  // value (geo-detected on this content page, post-mount) or the SSR server prop.
  const newRegion = useRegionCountryCode()

  const [address, setAddress] = useState(null)
  const [contactForm, updateContactForm] = useState({ name: '', email: '', subject: '', message: '', selectedCategory: categories.length && categories[0] })
  const [isLoading, setIsLoading] = useState(false)
  const [status, setStatus] = useState({ ticketId: null, error: false })

  // Contact address (side panel). TWO defects fixed here, 2026-08-28:
  //
  // 1. NO `.catch()`. `utils/clientHttp`'s `http.get` REJECTS on any non-2xx with
  //    `new Error('Request failed with status code ' + status)`. With nothing
  //    catching it, a failed fetch became an UNHANDLED PROMISE REJECTION, which
  //    Playwright reports as an uncaught `pageerror` — this is verbatim the
  //    console-health gate failure on /contact (run 33139102951):
  //      "/contact threw 1 FIRST-PARTY uncaught pageerror(s):
  //       Error: Request failed with status code 500"
  //    caused by /api/getContactAddress 500ing on a backend stall. ContactForm was
  //    the ONLY client fetch site in the app missing a catch (CoachesList,
  //    DataPacksList, OrderConfirmationPage, useRegionChange all have one), which
  //    is why /contact was the only route in the 25-route matrix that flaked.
  //    A failure now degrades to "no address block" — `<Grow in={!!address}>`
  //    already renders that state correctly.
  //
  // 2. `?region=${newRegion}` stringified a null into the LITERAL "null"
  //    (`/api/getContactAddress?region=null` in the Cloud Run logs).
  //    `useRegionCountryCode()` is null until the region store resolves geo
  //    post-mount, so every visit fired one `region=null` request and then a
  //    second with the real code. The server already maps `'null'` -> geo-IP
  //    (regionData.resolveCountryCodeForApi), so omitting the param entirely is
  //    behaviour-identical while halving the load on a known-flaky endpoint and
  //    keeping the logs honest. The unresolved-region request is deliberately
  //    still SENT (not skipped): if /api/getCountries is itself failing, the
  //    region store never resolves a code, and skipping would mean the address
  //    never loads at all.
  useEffect(() => {
    const query = newRegion ? `?region=${encodeURIComponent(newRegion)}` : ''
    let cancelled = false
    http
      .get(`/api/getContactAddress${query}`)
      .then((resp) => {
        if (!cancelled) setAddress(resp.data)
      })
      .catch((e) => {
        // Best-effort side content — never let it become an uncaught rejection.
        console.log('contact address unavailable', e.message)
      })
    return () => {
      cancelled = true
    }
  }, [newRegion])

  const submitContactForm = () => {
    resetStatus()
    setIsLoading(true)

    http.post('/api/contactForm', { ...contactForm, category: contactForm.selectedCategory.title })
      .then((response) => {
        console.log('response', response)
        setStatus({ ticketId: response.data.ticketId, error: false })
        const userData = {
          email: contactForm.email,
          first_name: contactForm.name,
        }
        pushDataLayerEvent('contact', {
          category: contactForm.selectedCategory.title,
          subject: contactForm.subject,
          user_data: userData,
        })
        pushDataLayerEvent('form_submit', {
          form_id: 'contact_form',
          form_category: contactForm.selectedCategory.title,
          user_data: userData,
        })
        resetContactForm()
      })
      .catch((e) => {
        console.log('Bad request', e.message)
        setStatus({ ticketId: null, error: true })
        resetContactForm()
      })
  }

  const resetStatus = () => {
    setStatus({ ticketId: null, error: false })
  }

  const resetContactForm = () => {
    setIsLoading(false)
    updateContactForm({ name: '', email: '', subject: '', message: '', selectedCategory: categories.length ? categories[0] : null })
  }

  return (
    <div style={{ backgroundColor: backgroundColor.value, color: textColor.value }}>
      <div className={styles.contentWrapper}>
        <div className={styles.formAndSideContentWrapper}>
          <div className={styles.formWrapper}>
            <form
              className={styles.form}
              onSubmit={(e) => {
                submitContactForm()
                e.preventDefault()
              }}
            >
              <Text type="h1">{title}</Text>
              {subtitle && <div className={styles.subtitleWrapper}><HighlightedText {...subtitle} /></div>}
              <SelectField
                labelText={'Category'}
                value={contactForm.selectedCategory._key}
                options={categories}
                description={contactForm.selectedCategory.description}
                onChange={(e) => updateContactForm({ ...contactForm, selectedCategory: categories.find((c) => c._key === e.target.value) })}
              />
              <InputField
                labelText={'First and last names'}
                value={contactForm.name}
                placeholder={'John Doe'}
                required
                onChange={(value) => updateContactForm({ ...contactForm, name: value })}
              />
              <InputField
                labelText={'Email'}
                value={contactForm.email}
                placeholder={'example@example.com'}
                type="email"
                required
                onChange={(value) => updateContactForm({ ...contactForm, email: value })}
              />
              <InputField
                labelText={'Subject'}
                value={contactForm.subject}
                placeholder={'Subject'}
                required
                onChange={(value) => updateContactForm({ ...contactForm, subject: value })}
              />
              <InputField
                labelText={'Message'}
                value={contactForm.message}
                placeholder={'Write your message here'}
                required
                multiline
                onChange={(value) => updateContactForm({ ...contactForm, message: value })}
              />

              <CtaLoading
                title="Send message"
                primary
                dark
                type="submit"
                isLoading={isLoading}
                disabled={isLoading}
              />
            </form>
            {
              <ContactFormSubmittedModal
                show={!!status.ticketId}
                ticketId={status.ticketId}
                onClose={resetStatus}
              />
            }
            {
              <ContactFormSubmittedAlert
                type="error"
                show={status.error}
                message={errorMessageText}
                onClose={resetStatus}
              />
            }
          </div>
          <div
            className={styles.sideContentWrapper}
            style={{
              backgroundColor: sideContent.backgroundColor.value,
              // task #67: sideImage is a ResolvedImage descriptor; styled bg takes .src
              backgroundImage: sideContent.sideImage?.src ? `url(${sideContent.sideImage.src})` : undefined
            }}
          >
            <Grow in={!!address} timeout={{ enter: 500 }}>
              <div className={styles.contactAddressesWrapper}>
                {address && renderContactAddress(address, false)}
              </div>
            </Grow>
            <Grow in={!!address} timeout={{ enter: 500 }}>
              <div>
                {sideContent.title && <SimpleBlockContent blocks={sideContent.title} noSpace/>}
                {sideContent.text && <SimpleBlockContent blocks={sideContent.text} noSpace/>}
                <div className={styles.ctasWrapper}>
                  {
                    sideContent.ctas.map(cta => <Cta {...cta} key={cta._key}/>)
                  }
                </div>
              </div>
            </Grow>
          </div>
        </div>
      </div>
    </div>
  )
}

const renderContactAddress = (address, highlight) => {
  return (
    <div className={`${styles.contactAddressWrapper}${highlight ? ' ' + styles.contactAddressWrapperHighlight : ''}`}>
      <Text type="h2">{address.name}</Text>
      <div className={styles.companyAddressWrapper}>
        {address.line1 && <Text type="body-short-2">{address.line1}</Text>}
        {address.line2 && <Text type="body-short-2">{address.line2}</Text>}
        {address.city && <Text type="body-short-2">{address.city}</Text>}
        {(address.state || address.postalCode) && <Text type="body-short-2">{`${address.state || ''} ${address.postalCode || ''}`}</Text>}
      </div>
      <div className={styles.emailAndPhoneWrapper}>
        <div className={styles.iconAndAddressWrapper}>
          <EmailIcon/>
          <Text type="body-short-2">{address.email}</Text>
        </div>
        <div className={styles.iconAndAddressWrapper}>
          <PhoneIcon/>
          <Text type="body-short-2">{address.phoneNumber}</Text>
        </div>
      </div>
    </div>
  )
}

export default ContactForm
